import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWallMass, WallMass } from "building-types/walls/wallTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import type { WallPath } from "building-types/walls/wallTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { getExteriorWallOffset, getExteriorWallSubstrateOffset } from "doc/utils/wallTypeTools";
import { intersectLine2Line2, CurveCurveRangeIntersection } from "geom/intersectLine2Line2";
import { line2Collinear } from "geom/line2";
import { offsetPolyLine2VariableStep } from "geom/offset";
import type { PlanarGraph } from "geom/planarGraph";
import {
  planarGraphPrevHalfEdgeOnCollinearStrip,
  planarGraphNextHalfEdgeOnCollinearStrip,
  planarGraphTraceFaceGroupPerimeter,
  planarGraphVertexStripToPolyLine2,
  planarGraphHalfEdgeTangent,
  planarGraphAdjacentHalfEdge,
} from "geom/planarGraph";
import {
  polyLine2Reverse,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeStart,
  polyLine2EdgeEnd,
  polyLine2EdgeTangent,
  polyLine2Edge,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2Dot } from "geom/vec2";
import { solidWallLayerWithFallback, wallMassLayers } from "./walls/wallLayers";

const COLLINEAR_WALL_EPSILON = 0.1;

function getWallMassOffset(
  solidWallMass: SolidWallMass,
  halfEdge: number,
  baseOffset: number = 0,
  overhangDatum: WallLateralDatumAnchor = WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
): number {
  if (!solidWallMass) {
    return 0;
  }

  const {
    leftOffset: leftWallOffset,
    rightOffset: rightWallOffset,
    path,
  } = solidWallLayerWithFallback(
    solidWallMass,
    overhangDatum === WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE
      ? WallLayerType.SUBSTRATE
      : WallLayerType.STRUCTURE,
  );
  const { halfEdgeIndex: wallHalfEdge, leftRoom, rightRoom } = path;
  const isLeft = halfEdge === wallHalfEdge;
  const room = isLeft ? leftRoom : rightRoom;
  const isExteriorWall = !room || !isWalledRoomEnclosure[room.attributes.roomEnclosure];

  if (!isExteriorWall) {
    return 0;
  }

  return (isLeft ? leftWallOffset : rightWallOffset) + baseOffset;
}

function getWallMassOffsetWithLayer(
  wall: WallMass,
  halfEdge: number,
  wallLayer: WallLayerType,
  wallPlanes: boolean,
): number {
  // TODO: remove the void wall mass filtering once we investigate the regressions
  // caused by them
  if (!wall || wall.wallType !== WallType.SOLID_MASS) {
    return 0;
  }

  let leftWallOffset: number;
  let rightWallOffset: number;
  let path: WallPath;

  const layers = wallMassLayers(wall, wallLayer, WallLOD.L2);

  if (layers.length === 0) {
    return 0;
  }

  if (wallPlanes) {
    // loop through all layers, regardless of wall layer type, to find the max offset
    leftWallOffset = -Infinity;
    rightWallOffset = -Infinity;
    for (const layer of layers) {
      const { leftOffset: leftOffsetCurrent, rightOffset: rightOffsetCurrent } = layer;
      if (leftOffsetCurrent > leftWallOffset) {
        leftWallOffset = leftOffsetCurrent;
      }
      if (rightOffsetCurrent > rightWallOffset) {
        rightWallOffset = rightOffsetCurrent;
      }
    }

    path = wall.path;
  } else {
    // get the offsets from the last layer
    leftWallOffset = layers[layers.length - 1].leftOffset;
    rightWallOffset = layers[layers.length - 1].rightOffset;
    path = layers[layers.length - 1].path;
  }

  const { halfEdgeIndex: wallHalfEdge, leftRoom, rightRoom } = path;
  const isLeft = halfEdge === wallHalfEdge;
  const room = isLeft ? leftRoom : rightRoom;
  const isExteriorWall = !room || !isWalledRoomEnclosure[room.attributes.roomEnclosure];

  if (!isExteriorWall) {
    return 0;
  }

  return isLeft ? leftWallOffset : rightWallOffset;
}

interface GetNextCollinearExteriorSolidWallMassOptions {
  readonly planarGraph: PlanarGraph;
  readonly wallMassSystem: WallMassSystem;
  readonly halfEdge: number;
  readonly reverse: boolean;
}

interface GetNextCollinearExteriorSolidWallMassResult {
  readonly solidWallMass: SolidWallMass | null;
  readonly halfEdge: number;
}

function getNextCollinearExteriorSolidWallMass({
  planarGraph,
  wallMassSystem,
  halfEdge,
  reverse,
}: GetNextCollinearExteriorSolidWallMassOptions): GetNextCollinearExteriorSolidWallMassResult {
  // look for first collinear exterior solid wall mass (if any)
  do {
    halfEdge = reverse
      ? planarGraphPrevHalfEdgeOnCollinearStrip(planarGraph, halfEdge)
      : planarGraphNextHalfEdgeOnCollinearStrip(planarGraph, halfEdge);

    if (halfEdge >= 0) {
      const solidWallMassId = wallMassSystem.halfEdgeToWall[halfEdge];
      const solidWallMass = wallMassSystem.walls[solidWallMassId];

      // if it's a solid wall mass
      if (solidWallMass?.wallType === WallType.SOLID_MASS) {
        const { path } = solidWallMass;
        const { halfEdgeIndex: wallHalfEdge, leftRoom, rightRoom } = path;
        const isLeft = halfEdge === wallHalfEdge;
        const room = isLeft ? leftRoom : rightRoom;
        const isExteriorWall = !room || !isWalledRoomEnclosure[room.attributes.roomEnclosure];

        // we only return exterior solid wall masses
        return isExteriorWall ? { solidWallMass, halfEdge } : { solidWallMass: null, halfEdge: -1 };
      }
    }
  } while (halfEdge >= 0);

  // no collinear exterior solid wall mass found
  return { solidWallMass: null, halfEdge: -1 };
}

interface GetRoofHalfEdgeOffsetOptions {
  readonly halfEdge: number;
  readonly wallMassSystem: WallMassSystem | null;
  readonly planarGraph: PlanarGraph;
  readonly wallSettings: WallSettings;
  readonly overhangDatum: WallLateralDatumAnchor;
  readonly baseOffset?: number;
}

function getRoofHalfEdgeOffset({
  halfEdge,
  wallMassSystem,
  planarGraph,
  wallSettings,
  overhangDatum,
  baseOffset = 0,
}: GetRoofHalfEdgeOffsetOptions) {
  if (!wallMassSystem) {
    return 0;
  }

  const exteriorWallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);
  const wallMassId = wallMassSystem.halfEdgeToWall[halfEdge];
  const wallMass = wallMassSystem.walls[wallMassId];

  if (wallMass?.path) {
    const isLeft = halfEdge === wallMass.path.halfEdgeIndex;
    const { leftRoom, rightRoom } = wallMass.path;
    const oppositeRoomEnclosure = isLeft ? rightRoom?.attributes?.roomEnclosure : leftRoom?.attributes?.roomEnclosure;

    //if exterior edge of porch offset exterior wall offset to get to centerline of porch
    if (!oppositeRoomEnclosure || !isWalledRoomEnclosure[oppositeRoomEnclosure]) {
      const { solidWallMass: nextSolidWallMass, halfEdge: nextHalfEdge } = getNextCollinearExteriorSolidWallMass({
        planarGraph,
        wallMassSystem,
        halfEdge,
        reverse: false,
      });

      const { solidWallMass: prevSolidWallMass, halfEdge: prevHalfEdge } = getNextCollinearExteriorSolidWallMass({
        planarGraph,
        wallMassSystem,
        halfEdge,
        reverse: true,
      });

      let offset = 0;

      // get the offset of the next and previous exterior solid wall masses (if any)
      if (nextSolidWallMass) {
        offset = Math.max(offset, getWallMassOffset(nextSolidWallMass, nextHalfEdge, baseOffset, overhangDatum));
      }

      if (prevSolidWallMass) {
        offset = Math.max(offset, getWallMassOffset(prevSolidWallMass, prevHalfEdge, baseOffset, overhangDatum));
      }

      // in there is no next or previous exterior solid wall mass...
      if (!nextSolidWallMass && !prevSolidWallMass) {
        offset = exteriorWallOffsetFromOverhangDatum + baseOffset;
      }

      return offset;
    }
  }

  // isolated void wall
  if (!wallMass || wallMass.wallType !== WallType.SOLID_MASS) {
    return 0;
  }

  // normal solid wall
  return getWallMassOffset(wallMass, halfEdge, baseOffset, overhangDatum);
}

interface GetRoofOffsetPerimetersOptions {
  readonly wallMassSystem: WallMassSystem | null;
  readonly planarGraph: PlanarGraph;
  readonly faceIndices: number[];
  readonly wallSettings: WallSettings;
  readonly overhangDatum: WallLateralDatumAnchor;
  readonly baseOffset?: number;
  readonly wallPlaneEnabled?: boolean;
}

export function getRoofOffsetPerimeters({
  wallMassSystem,
  planarGraph,
  faceIndices,
  wallSettings,
  overhangDatum,
  baseOffset = 0,
  wallPlaneEnabled = false,
}: GetRoofOffsetPerimetersOptions): PolyLine2[] {
  const { faceHalfEdges, faceVertices } = planarGraphTraceFaceGroupPerimeter(planarGraph, faceIndices);
  const perimeters: PolyLine2[] = [];

  // iterate over the faces
  for (let face = 0, faceCount = faceHalfEdges.length; face < faceCount; face++) {
    // build the perimeter for this face
    let perimeter = planarGraphVertexStripToPolyLine2(planarGraph, faceVertices[face], false);

    // Wall-plane cores sit on the room planar-graph edges. Omit the finish/cladding
    // offset so roof exact-outline edges stay coplanar with those wall planes.
    if (!wallPlaneEnabled) {
      const halfEdges = faceHalfEdges[face];
      const offsets: number[] = new Array<number>(halfEdges.length).fill(0);
      for (let halfEdgeIndex = 0, halfEdgeCount = halfEdges.length; halfEdgeIndex < halfEdgeCount; halfEdgeIndex++) {
        const halfEdge = halfEdges[halfEdgeIndex];

        offsets[halfEdgeIndex] = -getRoofHalfEdgeOffset({
          halfEdge,
          wallMassSystem,
          planarGraph,
          wallSettings,
          overhangDatum,
          baseOffset,
        });
      }

      perimeter = offsetPolyLine2VariableStep(perimeter, offsets).pl;
    }

    perimeter = polyLine2Reverse(perimeter);
    perimeters.push(perimeter);
  }

  return perimeters;
}

interface GetRoofEdgeWallOffsetWithLayerOptions {
  readonly wallMassSystem: WallMassSystem | null;
  readonly planarGraph: PlanarGraph;
  readonly edge: PolyLine2;
  readonly wallSettings: WallSettings;
  readonly wallLayerType: WallLayerType;
  readonly wallPlanes?: boolean;
}

export function getRoofEdgeWallOffsetWithLayer({
  wallMassSystem,
  planarGraph,
  edge,
  wallSettings,
  wallLayerType,
  wallPlanes = false,
}: GetRoofEdgeWallOffsetWithLayerOptions): number {
  let baseOffset = 0;

  switch (wallLayerType) {
    case WallLayerType.MASS:
      baseOffset = 0;
      break;
    case WallLayerType.STRUCTURE:
      baseOffset = getExteriorWallOffset(wallSettings);
      break;
    case WallLayerType.SUBSTRATE:
    case WallLayerType.FINISH:
      baseOffset = getExteriorWallSubstrateOffset(wallSettings);
      break;
    default:
      assertUnreachable(wallLayerType);
  }

  if (!wallMassSystem || !planarGraph) {
    return baseOffset;
  }

  const { walls } = wallMassSystem;
  let offset = baseOffset;

  const [ax, ay] = polyLine2EdgeStart(edge, 0);
  const [bx, by] = polyLine2EdgeEnd(edge, 0);
  const [t0x, t0y] = polyLine2EdgeTangent(edge, 0);

  // look for aligned walls with the edge
  for (const wallId in walls) {
    const wall = walls[wallId];
    const { path } = wall;
    const { startX, startY, endX, endY, halfEdgeIndex } = path;

    const intersection = intersectLine2Line2(ax, ay, bx, by, startX, startY, endX, endY, COLLINEAR_WALL_EPSILON);
    if (intersection && intersection instanceof CurveCurveRangeIntersection) {
      // get the half edge for this wall
      const [t1x, t1y] = planarGraphHalfEdgeTangent(planarGraph, halfEdgeIndex);
      const halfEdge = vec2Dot(t0x, t0y, t1x, t1y) > 0 ? planarGraphAdjacentHalfEdge(halfEdgeIndex) : halfEdgeIndex;
      const wallMassId = wallMassSystem.halfEdgeToWall[halfEdge];
      const wallMass = wallMassSystem.walls[wallMassId];

      // get the offset for this half edge
      const wallOffset = getWallMassOffsetWithLayer(wallMass, halfEdge, wallLayerType, wallPlanes);

      // update the offset for this edge
      if (Math.abs(wallOffset) > Math.abs(offset)) {
        offset = wallOffset;
      }
    }
  }

  return offset;
}

interface GetRoofPerimeterWallOffsetsWithLayerOptions {
  readonly wallMassSystem: WallMassSystem | undefined;
  readonly planarGraph: PlanarGraph | undefined;
  readonly perimeter: PolyLine2;
  readonly wallSettings: WallSettings;
  readonly wallLayerType: WallLayerType;
  readonly wallPlanes?: boolean;
}

export function getRoofPerimeterWallsOffsetsWithLayer({
  wallMassSystem,
  planarGraph,
  perimeter,
  wallSettings,
  wallLayerType,
  wallPlanes,
}: GetRoofPerimeterWallOffsetsWithLayerOptions): number[] {
  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(
    wallSettings,
    WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
  );
  const edgeCount = polyLine2EdgeCount(perimeter);
  const offsets: number[] = new Array<number>(edgeCount).fill(exteriorWallOffsetWithSheathing);

  if (!wallMassSystem || !planarGraph) {
    return offsets;
  }

  for (let i = 0; i < edgeCount; i++) {
    const edge = polyLine2Edge(perimeter, i);

    offsets[i] = getRoofEdgeWallOffsetWithLayer({
      wallMassSystem,
      planarGraph,
      edge,
      wallSettings,
      wallLayerType,
      wallPlanes,
    });
  }

  return offsets;
}

interface GetFreeFormRoofOffsetPerimeterOptions {
  readonly wallMassSystem: WallMassSystem | null;
  readonly planarGraph: PlanarGraph;
  readonly freeFormPerimeter: PolyLine2;
  readonly wallSettings: WallSettings;
  readonly overhangDatum: WallLateralDatumAnchor;
  readonly baseOffset?: number;
  readonly wallPlaneEnabled?: boolean;
}

export function getFreeFormRoofOffsetPerimeter({
  wallMassSystem,
  planarGraph,
  freeFormPerimeter,
  wallSettings,
  overhangDatum,
  baseOffset = 0,
  wallPlaneEnabled = false,
}: GetFreeFormRoofOffsetPerimeterOptions): PolyLine2 {
  if (!wallMassSystem || wallPlaneEnabled) {
    return polyLine2Copy(freeFormPerimeter);
  }

  const { walls } = wallMassSystem;
  const edgeCount = polyLine2EdgeCount(freeFormPerimeter);
  const offsets: number[] = new Array<number>(edgeCount).fill(0);

  // iterate the input perimeter edges
  for (let i = 0; i < edgeCount; i++) {
    const [ax, ay] = polyLine2EdgeStart(freeFormPerimeter, i);
    const [bx, by] = polyLine2EdgeEnd(freeFormPerimeter, i);
    const [t0x, t0y] = polyLine2EdgeTangent(freeFormPerimeter, i);

    // look for aligned walls with the current edge
    for (const wallId in walls) {
      const wall = walls[wallId];
      const { path } = wall;
      const { startX, startY, endX, endY, halfEdgeIndex } = path;

      const intersection = intersectLine2Line2(ax, ay, bx, by, startX, startY, endX, endY, COLLINEAR_WALL_EPSILON);
      if (intersection && intersection instanceof CurveCurveRangeIntersection) {
        // get the half edge for this wall
        const [t1x, t1y] = planarGraphHalfEdgeTangent(planarGraph, halfEdgeIndex);
        const halfEdge = vec2Dot(t0x, t0y, t1x, t1y) > 0 ? halfEdgeIndex : planarGraphAdjacentHalfEdge(halfEdgeIndex);

        // get the offset for this half edge
        const offset = -getRoofHalfEdgeOffset({
          halfEdge,
          wallMassSystem,
          planarGraph,
          wallSettings,
          overhangDatum,
          baseOffset,
        });

        // update the offset for this edge
        if (Math.abs(offset) > Math.abs(offsets[i])) {
          offsets[i] = offset;
        }
      }
    }
  }

  // to avoid unclosed perimeters without adding a new edge, we make sure the
  // first and last offsets are the same when the first and last edges are
  // collinear
  if (edgeCount > 0) {
    const firstEdge = polyLine2Edge(freeFormPerimeter, 0);
    const lastEdge = polyLine2Edge(freeFormPerimeter, edgeCount - 1);

    if (
      offsets[0] !== offsets[edgeCount - 1] &&
      line2Collinear(
        firstEdge[0],
        firstEdge[1],
        firstEdge[2],
        firstEdge[3],
        lastEdge[0],
        lastEdge[1],
        lastEdge[2],
        lastEdge[3],
      )
    ) {
      if (Math.abs(offsets[0]) > Math.abs(offsets[edgeCount - 1])) {
        offsets[edgeCount - 1] = offsets[0];
      } else {
        offsets[0] = offsets[edgeCount - 1];
      }
    }
  }

  // build the perimeter
  return offsetPolyLine2VariableStep(freeFormPerimeter, offsets).pl;
}
